import { useState, useEffect } from "react";
import { Brain, Info } from "lucide-react";
import { Aianalysis } from "../Aireports/Aianalysis";

export function LabReportAIAnalysis({ isOpen, onClose, reportTitle, reportFile }) {
  const [analyzing, setAnalyzing] = useState(true);
  const [progress, setProgress] = useState(0);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [aiMeta, setAiMeta] = useState(null);
  const [activeTab, setActiveTab] = useState("summary");

  useEffect(() => {
    if (isOpen && reportFile) {
      setAnalyzing(true);
      setProgress(0);
      setAnalysisResult(null);
      setAiMeta(null);

      const interval = setInterval(() => {
        setProgress((prev) => {
          const next = prev + 10;
          if (next >= 100) clearInterval(interval);
          return Math.min(next, 100);
        });
      }, 300);

      const analyze = async () => {
        try {
          const result = await Aianalysis(reportFile);
          const parsed = parseAIResponse(result.choices[0].message.content);
          setAnalysisResult(parsed);
          setAiMeta(result);
        } catch (err) {
          console.error("AI Analysis Error:", err);
          setAnalysisResult({
            summary: ["AI analysis failed."],
            recommendations: ["Unable to provide recommendations due to an error."],
            results: []
          });
        } finally {
          const delay = 2000;
          const startTime = Date.now();
          const elapsed = Date.now() - startTime;

          const remaining = delay - elapsed;
          setTimeout(() => setAnalyzing(false), Math.max(remaining, 0));
        }
      };

      analyze();
      return () => clearInterval(interval);
    }
  }, [isOpen, reportFile]);


const parseAIResponse = (text) => {
  if (!text || typeof text !== "string") {
    return {
      summary: ["No summary found."],
      recommendations: ["No recommendations provided."],
      results: []
    };
  }

  text = text.replace(/```(?:markdown)?\n?/gi, "").replace(/```$/, "").trim();

  const sectionRegex = /##\s*Summary\s*([\s\S]*?)(?=\n##|$)/i;
  const recommendationsRegex = /##\s*Recommendations\s*([\s\S]*?)(?=\n##|$)/i;

  const summaryMatch = text.match(sectionRegex);
  const recommendationsMatch = text.match(recommendationsRegex);

  const extractBullets = (block) =>
    block
      .split(/\n+/)
      .filter(line => /^[-•]/.test(line.trim()))
      .map(line => line.replace(/^[-•]\s*/, "").trim());

  const extractResults = (block) => {
    const resultLines = block
      .split("\n")
      .filter(line =>
        /^[-•]?\s*\*?\**[\w\s()]+[:：]\s*[\d.]+/.test(line)
      );

    return resultLines.map(line => {
      const match = line.match(/^\s*[-•]?\s*\**([\w\s()/]+)\**[:：]\s*([\d.]+)\s*([a-zA-Z%/^\d]*)\s*(?:[-–—]\s*(.*))?/);
      if (!match) return null;

      const [, name, value, unit, extra] = match;
      const status = extra?.toLowerCase().includes("low")
        ? "low"
        : extra?.toLowerCase().includes("high")
        ? "high"
        : "normal";

      return {
        name: name.trim(),
        value: parseFloat(value),
        unit: unit.trim(),
        normalRange: extra?.match(/\((.*?)\)/)?.[1] || "N/A",
        status,
      };
    }).filter(Boolean);
  };

  return {
    summary: extractBullets(summaryMatch?.[1] || ""),
    recommendations: extractBullets(recommendationsMatch?.[1] || ""),
    followUp: "Schedule a consultation with your physician to discuss abnormal results if any.",
    results: extractResults(summaryMatch?.[1] || ""),
  };
};


  // const parseAIResponse = (text) => {
  //   if (!text || typeof text !== "string") {
  //     return {
  //       summary: ["No summary found."],
  //       recommendations: ["No recommendations provided."],
  //       results: []
  //     };
  //   }

  //   const summaryMatch = text.match(/##\s*Summary\s*(.*?)(?=##\s*Recommendations|$)/s);
  //   const recommendationsMatch = text.match(/##\s*Recommendations\s*(.*)/s);
  //   const detailsMatch = text.match(/##\s*Details\s*(.*?)(?=##|$)/s);

  //   const summaryRaw = summaryMatch ? summaryMatch[1].trim() : "";
  //   const recommendationsRaw = recommendationsMatch ? recommendationsMatch[1].trim() : "";

  //   const extractBullets = (block) =>
  //     block.split(/\n+/).filter(line => line.trim().startsWith("-")).map(line => line.replace(/^[-\u2022]+\s*/, "").trim());

  //   const mockResults = [
  //     { name: "Hemoglobin", value: 13.5, unit: "g/dL", normalRange: "13.0 - 17.0", status: "normal" },
  //     { name: "WBC", value: 11.2, unit: "x10^3/uL", normalRange: "4.0 - 10.0", status: "high" },
  //   ];

  //   return {
  //     summary: extractBullets(summaryRaw),
  //     recommendations: extractBullets(recommendationsRaw),
  //     followUp: "Schedule a consultation with your physician to discuss abnormal WBC levels.",
  //     results: mockResults
  //   };
  // };

  const getStatusIcon = (status) => {
    if (status === "normal") return <span className="h-2 w-2 rounded-full bg-green-500"></span>;
    if (status === "high" || status === "low") return <span className="h-2 w-2 rounded-full bg-amber-500"></span>;
    return <span className="h-2 w-2 rounded-full bg-red-500"></span>;
  };

  const getStatusText = (status) => {
    if (status === "normal") return "Normal";
    if (status === "high") return "High";
    if (status === "low") return "Low";
    return "Critical";
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-9999 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose}></div>
      <div className="dialog-content sm:max-w-[600px]">
        <div className="dialog-header">
          <div className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-semibold">AI Analysis: {reportTitle}</h3>
          </div>
          <p className="text-sm text-muted-foreground">AI-powered insights to help you understand your lab results</p>
        </div>

        {analyzing ? (
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-center">
              <div className="h-16 w-16 rounded-full border-4 border-primary/30 border-t-primary animate-spin"></div>
            </div>
            <div className="text-center">
              <h3 className="text-lg font-medium">Analyzing your lab report...</h3>
              <p className="text-sm text-muted-foreground">Our AI is reviewing your results and preparing insights</p>
            </div>
            <div className="progress">
              <div className="progress-indicator" style={{ width: `${progress}%` }}></div>
            </div>
          </div>
        ) : (
          analysisResult && (
            <div className="py-4">
              <div className="tabs">
                <div className="tabs-list">
                  <button className="tabs-trigger" data-state={activeTab === "summary" ? "active" : "inactive"} onClick={() => setActiveTab("summary")}>Summary</button>
                  {/* <button className="tabs-trigger" data-state={activeTab === "details" ? "active" : "inactive"} onClick={() => setActiveTab("details")}>Detailed Results</button> */}
                  <button className="tabs-trigger" data-state={activeTab === "recommendations" ? "active" : "inactive"} onClick={() => setActiveTab("recommendations")}>Recommendations</button>
                </div>

                <div className="tabs-content" data-state={activeTab === "summary" ? "active" : "inactive"}>
                  <div className="space-y-4 pt-4">
                    <div className="rounded-lg border bg-card p-4 text-card-foreground shadow-sm">
                      <h3 className="mb-2 font-medium">Summary Analysis</h3>
                      <ul>{analysisResult.summary.map((s, i) => (<li key={i}>{s}</li>))}</ul>
                    </div>
                    <div className="rounded-lg border bg-card p-4 text-card-foreground shadow-sm">
                      <h3 className="mb-2 font-medium">Follow-up Recommendation</h3>
                      <p>{analysisResult.followUp}</p>
                    </div>
                  </div>
                </div>

                <div className="tabs-content" data-state={activeTab === "details" ? "active" : "inactive"}>
                  <div className="space-y-4 pt-4">
                    <div className="rounded-lg border shadow-sm">
                      <div className="bg-muted/50 p-3">
                        <h3 className="font-medium">Detailed Results</h3>
                      </div>
                      <div className="p-0">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b bg-muted/30">
                              <th className="px-4 py-2 text-left font-medium">Test</th>
                              <th className="px-4 py-2 text-left font-medium">Result</th>
                              <th className="px-4 py-2 text-left font-medium">Normal Range</th>
                              <th className="px-4 py-2 text-left font-medium">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {analysisResult.results.map((result, index) => (
                              <tr key={index} className="border-b last:border-0">
                                <td className="px-4 py-3">{result.name}</td>
                                <td className="px-4 py-3">{result.value} {result.unit}</td>
                                <td className="px-4 py-3">{result.normalRange}</td>
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-1">
                                    {getStatusIcon(result.status)}
                                    <span className={
                                      result.status === "normal" ? "text-green-600" :
                                        result.status === "critical" ? "text-red-600" : "text-amber-600"
                                    }>
                                      {getStatusText(result.status)}
                                    </span>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="tabs-content" data-state={activeTab === "recommendations" ? "active" : "inactive"}>
                  <div className="space-y-4 pt-4">
                    <div className="rounded-lg border bg-card p-4 text-card-foreground shadow-sm">
                      <h3 className="mb-2 font-medium">Lifestyle Recommendations</h3>
                      <ul className="space-y-2">
                        {analysisResult.recommendations.map((rec, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <div className="mt-1 h-1.5 w-1.5 rounded-full bg-primary"></div>
                            <span>{rec}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="rounded-md bg-blue-50 p-4 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
                      <div className="flex items-start">
                        <Info className="mr-2 mt-0.5 h-4 w-4" />
                        <div>
                          <h4 className="font-medium">Important Note</h4>
                          <p className="text-sm">
                            This AI analysis is for informational purposes only and does not replace professional medical advice. Always consult with your healthcare provider regarding your lab results.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )
        )}

        <div className="dialog-footer">
          <button className="button button-outline" onClick={onClose}>Close</button>
          {/* {!analyzing && <button className="button button-default">Download Analysis</button>} */}
        </div>
      </div>
    </div>
  );
}
